/**
 * TipTap mangles Jinja control tags inside <ol>/<ul> (e.g. `{% for %}` becomes
 * its own <li>), which breaks email preview into raw arrays or broken markup.
 *
 * - toEditorHtml: rewrite canonical list loops into paragraph form TipTap keeps
 * - fromEditorHtml: repair mangled loops + unwrap control tags + restore <ol>/<ul>
 */

const IDENT_RE = '[a-zA-Z_][a-zA-Z0-9_]*';

/** Variables that must never be inserted as bare `{{ name }}` (array dump). */
const LIST_VARIABLES = new Set(['questions']);

export function isListVariable(name) {
  const n = String(name || '').trim().toLowerCase();
  if (!n) return false;
  if (LIST_VARIABLES.has(n)) return true;
  return /(_list|_items|_questions)$/.test(n);
}

/**
 * TipTap turns
 * `<ol>{% for q in questions %}<li>{{ q }}</li>{% endfor %}</ol>`
 * into three list items. Rebuild the canonical loop.
 *
 * Capture groups: 1=ol|ul, 2=item, 3=list. Item backref in the pattern is \2.
 */
export function repairMangledListLoops(html) {
  let out = String(html || '');
  const mangled = new RegExp(
    `<(ol|ul)\\s*>\\s*`
    + `<li\\b[^>]*>\\s*(?:<p\\b[^>]*>\\s*)?`
    + `\\{%\\s*for\\s+(${IDENT_RE})\\s+in\\s+(${IDENT_RE})\\s*%\\}`
    + `\\s*(?:</p>\\s*)?</li>\\s*`
    + `<li\\b[^>]*>\\s*(?:<p\\b[^>]*>\\s*)?`
    + `\\{\\{\\s*\\2\\s*\\}\\}`
    + `\\s*(?:</p>\\s*)?</li>\\s*`
    + `<li\\b[^>]*>\\s*(?:<p\\b[^>]*>\\s*)?`
    + `\\{%\\s*endfor\\s*%\\}`
    + `\\s*(?:</p>\\s*)?</li>\\s*`
    + `</\\1>`,
    'gi',
  );
  out = out.replace(mangled, '<$1>{% for $2 in $3 %}<li>{{ $2 }}</li>{% endfor %}</$1>');
  return out;
}

/** `<p>{% if x %}</p>` → `{% if x %}` (and other control tags). */
export function unwrapJinjaControlParagraphs(html) {
  return String(html || '').replace(
    /<p(?:\s[^>]*)?>\s*(\{%[\s\S]*?%\})\s*<\/p>/gi,
    '$1',
  );
}

/**
 * Paragraph-body loops (TipTap-safe) → email `<ol>` / `<ul>` lists.
 * Also accepts `{{ loop.index }}. {{ item }}` and `• {{ item }}` bodies.
 */
export function canonicalizeParagraphLoops(html) {
  let out = String(html || '');

  // Bullet paragraphs → ul
  out = out.replace(
    new RegExp(
      `\\{%\\s*for\\s+(${IDENT_RE})\\s+in\\s+(${IDENT_RE})\\s*%\\}\\s*`
      + `<p(?:\\s[^>]*)?>\\s*[•\\-\\*]\\s*\\{\\{\\s*\\1\\s*\\}\\}\\s*</p>\\s*`
      + `\\{%\\s*endfor\\s*%\\}`,
      'gi',
    ),
    '<ul>{% for $1 in $2 %}<li>{{ $1 }}</li>{% endfor %}</ul>',
  );

  // Numbered / plain paragraphs → ol
  out = out.replace(
    new RegExp(
      `\\{%\\s*for\\s+(${IDENT_RE})\\s+in\\s+(${IDENT_RE})\\s*%\\}\\s*`
      + `<p(?:\\s[^>]*)?>\\s*`
      + `(?:\\{\\{\\s*loop\\.index\\s*\\}\\}\\s*\\.\\s*)?`
      + `\\{\\{\\s*\\1\\s*\\}\\}\\s*`
      + `</p>\\s*`
      + `\\{%\\s*endfor\\s*%\\}`,
      'gi',
    ),
    '<ol>{% for $1 in $2 %}<li>{{ $1 }}</li>{% endfor %}</ol>',
  );

  return out;
}

/**
 * Canonical seed / storage HTML → TipTap-safe HTML (no `{% for %}` inside ol/ul).
 */
export function toEditorHtml(html) {
  let out = String(html || '');

  out = out.replace(
    new RegExp(
      `<(ol|ul)\\s*>\\s*`
      + `\\{%\\s*for\\s+(${IDENT_RE})\\s+in\\s+(${IDENT_RE})\\s*%\\}\\s*`
      + `<li\\b[^>]*>\\s*\\{\\{\\s*\\2\\s*\\}\\}\\s*</li>\\s*`
      + `\\{%\\s*endfor\\s*%\\}\\s*`
      + `</\\1>`,
      'gi',
    ),
    (_m, tag, item, list) => {
      if (String(tag).toLowerCase() === 'ul') {
        return `{% for ${item} in ${list} %}<p>• {{ ${item} }}</p>{% endfor %}`;
      }
      return `{% for ${item} in ${list} %}<p>{{ loop.index }}. {{ ${item} }}</p>{% endfor %}`;
    },
  );

  // Bare control tags between blocks → TipTap will wrap them; leave as-is.
  return out;
}

/**
 * Editor HTML → storage / preview HTML that renders proper lists.
 */
export function fromEditorHtml(html) {
  let out = String(html || '');
  out = repairMangledListLoops(out);
  out = unwrapJinjaControlParagraphs(out);
  out = canonicalizeParagraphLoops(out);
  // Second unwrap pass after loop rewrite may leave control tags in paragraphs.
  out = unwrapJinjaControlParagraphs(out);
  return out;
}
